/****************************************************************************
 * Copyright 2021 EPAM Systems
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 ***************************************************************************/

import type { ReStruct } from '../../render';

import { BaseOperation } from './BaseOperation';
import type { StructProperty } from 'domain/entities/struct';
import { OperationType } from './OperationType';

class FragmentDelete extends BaseOperation {
  readonly frid: number;
  static InverseConstructor: new (
    fragmentId?: number | null,
    properties?: Array<StructProperty>,
  ) => BaseOperation;

  constructor(fragmentId: number) {
    super(OperationType.FRAGMENT_DELETE, 100);
    this.frid = fragmentId;
  }

  execute(restruct: ReStruct) {
    const struct = restruct.molecule;
    if (!struct.frags.get(this.frid)) {
      return;
    }

    BaseOperation.invalidateItem(restruct, 'frags', this.frid, 1);
    restruct.frags.delete(this.frid);
    restruct.markItemRemoved(); // notifyFragmentRemoved
    struct.frags.delete(this.frid);

    const enhancedFalg = restruct.enhancedFlags.get(this.frid);
    if (!enhancedFalg) return;
    restruct.clearVisel(enhancedFalg.visel);
    restruct.enhancedFlags.delete(this.frid);
  }

  invert() {
    return new FragmentDelete.InverseConstructor(this.frid);
  }
}

export { FragmentDelete };
